<template>
  <div class="p-1.5 md:p-3 bg-gray-50 dark:bg-gray-900 min-h-screen font-sans">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-3">
      <div class="flex items-center space-x-3">
        <div class="h-8 w-1 bg-gradient-to-b from-green-500 to-emerald-600 rounded-full"></div>
        <div>
          <h1 class="text-xl md:text-2xl font-bold text-gray-800 dark:text-white">Recepción y Reparación</h1>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 flex items-center mt-0.5">
            <Icon icon="mdi:flash-outline" class="mr-1" />
            SYSTEM JJ - Taller de Servicio Técnico
          </p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <!-- 🔥 INDICADOR DE ACTUALIZACIÓN -->
        <span class="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
          <Icon :icon="pollingActive ? 'mdi:clock' : 'mdi:clock-outline'" class="text-xs" />
          {{ pollingActive ? 'Actualizando...' : 'Auto-actualización' }}
        </span>
        <button @click="resetForm" class="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm hover:shadow flex items-center gap-2 text-xs font-medium">
          <Icon icon="mdi:refresh" class="text-base" />
          Limpiar Formulario
        </button>
      </div>
    </div>

    <!-- Formulario en dos columnas -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <!-- Columna izquierda -->
      <div class="space-y-3">
        <!-- Tarjeta de Cliente -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700">
          <div class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-base font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg mr-2">
                <Icon icon="mdi:account" class="text-green-600 dark:text-green-400 text-lg" />
              </div>
              Información del Cliente
              <span class="ml-2 text-[10px] bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded-full">* Requerido</span>
            </h2>
          </div>
          <div class="p-3">
            <!-- Pestañas -->
            <div class="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-0.5 rounded-xl mb-3">
              <button @click="modoCliente = 'buscar'" :class="['flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200', modoCliente === 'buscar' ? 'bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200']">
                <Icon icon="mdi:magnify" class="inline mr-1" /> Buscar
              </button>
              <button @click="modoCliente = 'crear'" :class="['flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200', modoCliente === 'crear' ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200']">
                <Icon icon="mdi:account-plus" class="inline mr-1" /> Nuevo
              </button>
            </div>

            <!-- Modo Buscar Cliente -->
            <div v-if="modoCliente === 'buscar'" class="relative">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Buscar Cliente Existente</label>
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input v-model="buscarCliente" type="text" placeholder="Nombre, documento, teléfono o email..." class="w-full pl-10 pr-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-green-500 text-sm transition-all" @input="debounceBuscarClientes" @focus="mostrarClientesSiHay" />
              </div>
              
              <!-- Dropdown de resultados -->
              <div v-if="mostrarListaClientes && clientesEncontrados.length > 0" class="absolute mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-h-72 overflow-y-auto z-50">
                <div v-for="(cliente, index) in clientesEncontrados" :key="cliente.idCliente || cliente.id || index" @click="seleccionarCliente(cliente)" class="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                  <div class="flex items-start gap-3">
                    <div class="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <Icon icon="mdi:account-circle" class="text-green-600 dark:text-green-400 text-xl" />
                    </div>
                    <div class="flex-1">
                      <div class="flex justify-between items-start">
                        <div>
                          <p class="font-semibold text-gray-800 dark:text-white">{{ cliente.nombre }} {{ cliente.apellido }}</p>
                          <p class="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{{ formatearTipoDocumento(cliente.tipoDocumento) }}: <span class="font-mono">{{ cliente.documento }}</span></p>
                          <div class="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span v-if="cliente.telefono" class="flex items-center"><Icon icon="mdi:phone" class="mr-1 text-xs" />{{ cliente.telefono }}</span>
                          </div>
                        </div>
                        <Icon v-if="form.idCliente === cliente.idCliente" icon="mdi:check-circle" class="text-green-500 text-2xl" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-if="buscarCliente && buscarCliente.trim().length >= 2 && !cargandoClientes && clientesEncontrados.length === 0" class="absolute mt-2 w-full p-6 text-center bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 z-50">
                <Icon icon="mdi:account-search" class="text-4xl text-gray-400 mx-auto mb-2" />
                <p class="text-gray-600 dark:text-gray-400 mb-3">No encontramos "{{ buscarCliente }}"</p>
                <button @click="modoCliente = 'crear'" class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2">
                  <Icon icon="mdi:account-plus" />Crear este cliente
                </button>
              </div>
              
              <div v-if="cargandoClientes" class="absolute mt-2 w-full p-4 text-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-2xl z-50">
                <div class="inline-block animate-spin rounded-full h-5 w-5 border-2 border-green-500 border-t-transparent"></div>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">Buscando clientes...</p>
              </div>
            </div>

            <!-- Modo Crear Cliente -->
            <div v-if="modoCliente === 'crear'">
              <form @submit.prevent="guardarNuevoCliente" class="space-y-4">
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo Documento <span class="text-red-500">*</span></label>
                    <select v-model="nuevoCliente.tipoDocumento" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all">
                      <option value="">Seleccionar...</option>
                      <option value="cedula">Cédula de Identidad</option>
                      <option value="rif">RIF</option>
                      <option value="pasaporte">Pasaporte</option>
                      <option value="identificacion_extranjera">Identificación Extranjera</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Número Documento <span class="text-red-500">*</span></label>
                    <input v-model="nuevoCliente.documento" type="text" :placeholder="getPlaceholderDocumento(nuevoCliente.tipoDocumento)" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" @blur="formatearDocumentoInput" />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre <span class="text-red-500">*</span></label>
                    <input v-model="nuevoCliente.nombre" type="text" placeholder="Ej: Juan Carlos" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" @blur="nuevoCliente.nombre = toTitleCase(nuevoCliente.nombre)" />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido</label>
                    <input v-model="nuevoCliente.apellido" type="text" placeholder="Ej: Rodríguez" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" @blur="nuevoCliente.apellido = toTitleCase(nuevoCliente.apellido)" />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono</label>
                    <input v-model="nuevoCliente.telefono" type="text" placeholder="0414-7470715" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" @blur="formatearTelefonoInput" />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <input v-model="nuevoCliente.email" type="email" placeholder="ejemplo@email.com" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" />
                  </div>
                  <div class="col-span-2">
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Dirección <span class="text-red-500">*</span></label>
                    <textarea v-model="nuevoCliente.direccion" rows="2" placeholder="Dirección completa del cliente..." class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all resize-none" @blur="nuevoCliente.direccion = toTitleCase(nuevoCliente.direccion)"></textarea>
                  </div>
                </div>
                <div class="flex gap-3 pt-2">
                  <button type="submit" :disabled="!puedeGuardarCliente || guardandoCliente" :class="['flex-1 px-4 py-2 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 font-medium', puedeGuardarCliente && !guardandoCliente ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed']">
                    <Icon v-if="guardandoCliente" icon="mdi:loading" class="animate-spin text-lg" />
                    <Icon v-else icon="mdi:account-plus" class="text-lg" />
                    {{ guardandoCliente ? 'Guardando...' : 'Guardar y Seleccionar' }}
                  </button>
                  <button type="button" @click="modoCliente = 'buscar'" class="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200">Cancelar</button>
                </div>
              </form>
            </div>
            
            <!-- Cliente Seleccionado -->
            <div v-if="clienteSeleccionado" class="mt-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800 rounded-xl border border-green-200 dark:border-green-800/50">
              <div class="flex items-start gap-3">
                <div class="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Icon icon="mdi:account-check" class="text-green-600 dark:text-green-400 text-2xl" />
                </div>
                <div class="flex-1">
                  <div class="flex justify-between items-start">
                    <div>
                      <p class="font-semibold text-gray-800 dark:text-white text-lg">{{ clienteSeleccionado.nombre }} {{ clienteSeleccionado.apellido }}</p>
                      <p class="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{{ formatearTipoDocumento(clienteSeleccionado.tipoDocumento) }}: <span class="font-mono">{{ clienteSeleccionado.documento }}</span></p>
                      <div class="flex flex-wrap gap-3 mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <span v-if="clienteSeleccionado.telefono" class="flex items-center"><Icon icon="mdi:phone" class="mr-1 text-gray-400" />{{ clienteSeleccionado.telefono }}</span>
                      </div>
                    </div>
                    <button @click="deseleccionarCliente" class="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200" title="Cambiar cliente">
                      <Icon icon="mdi:close" class="text-xl" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Información de la Recepción -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700">
          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-base font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-2">
                <Icon icon="mdi:file-document-edit" class="text-blue-600 dark:text-blue-400 text-lg" />
              </div>
              Detalles de la Orden
            </h2>
          </div>
          <div class="p-3 space-y-3">
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">N° Orden</label>
                <div class="relative">
                  <Icon icon="mdi:tag" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <input :value="numeroOrdenDisplay" type="text" readonly class="w-full pl-9 pr-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 cursor-not-allowed font-mono text-xs" />
                </div>
              </div>
              <div>
                <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Sede / Taller <span class="text-red-500">*</span></label>
                <div class="relative">
                  <Icon icon="mdi:store-marker" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <select v-model="form.idSede" @change="actualizarNombreTaller" class="w-full pl-9 pr-3 py-1.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-500 text-xs transition-all">
                    <option value="null">-- Sede --</option>
                    <option v-for="sede in sedesList" :key="sede.idSede" :value="sede.idSede">{{ sede.nombre }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Código Producto / Equipo <span class="text-red-500">*</span></label>
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input v-model="buscarProducto" @keyup.f2="mostrarModalF2Productos" type="text" placeholder="Código o nombre... (F2 lista)" class="w-full pl-10 pr-12 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" @input="debounceBuscarProductos" @focus="mostrarProductosSiHay" />
                <button @click="mostrarModalF2Productos" title="Ver Lista Completa (F2)" class="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded shadow-sm text-[10px] border border-blue-200 hover:bg-blue-200 font-bold transition-colors">LISTA F2</button>
                
                <div v-if="mostrarListaProductos && productosEncontrados.length > 0" class="absolute mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-h-60 overflow-y-auto z-50">
                  <div v-for="ps in productosEncontrados" :key="ps.idProductoServicio" @click="seleccionarProducto(ps)" class="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                    <div class="flex justify-between items-center text-sm">
                      <div>
                        <p class="font-bold text-gray-800 dark:text-white">{{ ps.codigo }}</p>
                        <p class="text-[11px] text-gray-600 dark:text-gray-400">{{ ps.nombre }}</p>
                      </div>
                      <Icon v-if="form.idProductoServicios === ps.idProductoServicio" icon="mdi:check-circle" class="text-green-500" />
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="productoSeleccionado" class="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50 relative">
                <div class="flex items-center gap-2">
                  <div class="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Icon icon="mdi:barcode-scan" class="text-blue-600 dark:text-blue-400 text-base" />
                  </div>
                  <div class="flex-1 overflow-hidden">
                    <p class="text-xs font-bold text-gray-800 dark:text-white truncate">{{ productoSeleccionado.codigo }} - {{ productoSeleccionado.nombre }}</p>
                  </div>
                  <button @click="deseleccionarProducto" class="text-gray-400 hover:text-red-500 transition-colors">
                    <Icon icon="mdi:close-circle" class="text-lg" />
                  </button>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Descripción <span class="text-red-500">*</span></label>
                <div class="relative">
                  <Icon icon="mdi:text-box-edit-outline" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <input v-model="form.descripcion" type="text" placeholder="Equipo o servicio..." class="w-full pl-9 pr-3 py-1.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-500 text-xs transition-all" @blur="form.descripcion = toTitleCase(form.descripcion)" />
                </div>
              </div>
              <div>
                <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Diagnóstico <span class="text-red-500">*</span></label>
                <div class="relative">
                  <Icon icon="mdi:stethoscope" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <select v-model="form.tipoDiagnostico" @change="actualizarMontoDiagnostico" class="w-full pl-9 pr-3 py-1.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-500 text-xs font-bold transition-all">
                    <option value="GRATIS">GRATIS</option>
                    <option value="EXPRESS">EXPRESS (10$)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna derecha -->
      <div class="space-y-3">
        <!-- Detalles del Equipo -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700">
          <div class="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-800 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-base font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg mr-2">
                <Icon icon="mdi:laptop" class="text-orange-600 dark:text-orange-400 text-lg" />
              </div>
              Estado del Equipo
            </h2>
          </div>
          <div class="p-3 space-y-3">
            <div>
              <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Problema Reportado <span class="text-red-500">*</span></label>
              <textarea v-model="form.problema" rows="4" placeholder="Fallo o motivo de ingreso..." class="w-full px-3 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-1 focus:ring-orange-500 text-sm resize-none transition-all" @blur="form.problema = toTitleCase(form.problema)"></textarea>
            </div>
            <div>
              <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Accesorios / Clave <span class="text-red-500">*</span></label>
              <div class="relative">
                <Icon icon="mdi:lock-pattern" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                <textarea v-model="form.accesorios" rows="3" maxlength="200" placeholder="Cargador, Cover, Clave..." class="w-full pl-9 pr-3 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-1 focus:ring-orange-500 text-sm resize-none transition-all" @input="limitarAccesorios" @blur="form.accesorios = toTitleCase(form.accesorios)"></textarea>
              </div>
              <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Máximo 200 caracteres · {{ form.accesorios.length }}/200</p>
            </div>
          </div>
        </div>

        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700">
          <div class="bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-base font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 bg-teal-100 dark:bg-teal-900/30 rounded-lg mr-2">
                <Icon icon="mdi:calendar-clock" class="text-teal-600 dark:text-teal-400 text-lg" />
              </div>
              Línea de Tiempo (Fechas)
            </h2>
          </div>
          <div class="p-3 space-y-3">
            <div class="grid grid-cols-1 gap-3">
              <div>
                <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Fecha de Entrada</label>
                <div class="relative">
                  <Icon icon="mdi:login" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <input v-model="form.fechaIngreso" type="datetime-local" class="w-full pl-9 pr-2 py-1.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 font-mono text-xs outline-none" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Asignación -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700">
          <div class="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-gray-800 dark:to-gray-800 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-base font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-2">
                <Icon icon="mdi:account-wrench" class="text-purple-600 dark:text-purple-400 text-lg" />
              </div>
              Asignación Técnica
            </h2>
          </div>
          <div class="p-3 space-y-3">
            <div>
              <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Técnico que Recibe <span class="text-red-500">*</span></label>
              <div class="relative">
                <div class="relative">
                  <Icon icon="mdi:account-hard-hat" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-500" />
                  <input 
                    :value="tecnicoSeleccionado ? `${tecnicoSeleccionado.nombre} ${tecnicoSeleccionado.apellido}` : 'Cargando...'" 
                    type="text" 
                    readonly 
                    class="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 cursor-not-allowed font-medium text-sm" 
                  />
                </div>
              </div>
            </div>

            <div class="pt-2">
              <button @click="submitRecepcion" :disabled="!puedeFinalizar || loading" :class="['w-full px-4 py-3 rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 font-bold text-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5', puedeFinalizar && !loading ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed grayscale']">
                <Icon v-if="loading" icon="mdi:loading" class="animate-spin text-xl" />
                <Icon v-else icon="mdi:check-circle-outline" class="text-xl" />
                {{ loading ? 'Sincronizando...' : 'FINALIZAR ORDEN' }}
              </button>
              
              <div v-if="!puedeFinalizar && !loading" class="mt-2 text-center">
                <p class="text-[10px] text-gray-400 italic">Complete los campos requeridos para finalizar</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Banner Compacto -->
        <div class="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-4 text-white shadow-xl relative overflow-hidden group border border-green-500/20">
          <div class="absolute -right-4 -bottom-4 opacity-10 group-hover:opacity-20 transition-all duration-700 rotate-12">
            <Icon icon="mdi:shield-check" class="text-[80px]" />
          </div>
          <div class="relative z-10 flex items-center gap-3">
            <div class="p-2 bg-green-500/20 rounded-xl backdrop-blur-md border border-green-500/30">
              <Icon icon="mdi:shield-lock" class="text-2xl text-green-400" />
            </div>
            <div>
              <h4 class="font-bold text-sm uppercase tracking-tight">Registro de Garantía</h4>
              <p class="text-[10px] text-gray-400 font-medium">Equipos respaldados en tiempo real.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODALES -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn">
          <div class="p-6 text-left">
            <div class="flex justify-center mb-4">
              <div class="p-3 rounded-full" :class="confirmModalIconClass">
                <Icon :icon="confirmModalIcon" class="text-4xl" :class="confirmModalIconColor" />
              </div>
            </div>
            <h3 class="text-xl font-bold text-center text-gray-900 dark:text-white mb-2">{{ confirmModalTitle }}</h3>
            <p class="text-center text-gray-600 dark:text-gray-400 mb-6" v-html="confirmModalMessage"></p>
            <div class="flex gap-3">
              <button v-if="confirmModalType !== 'info'" @click="cancelarConfirmacion" class="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 font-medium text-sm">Cancelar</button>
              <button @click="aceptarConfirmacion" class="flex-1 px-4 py-3 rounded-xl transition-all duration-200 font-bold text-white shadow-md text-sm" :class="confirmModalConfirmClass">{{ confirmModalConfirmText }}</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showF2Modal" class="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-2 sm:p-4">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="cerrarF2Modal"></div>
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-fadeIn border border-gray-200 dark:border-gray-700">
          <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
            <div class="flex items-center gap-3">
              <div :class="['p-2 rounded-lg', f2ModalType === 'productos' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30']">
                 <Icon :icon="f2ModalType === 'productos' ? 'mdi:package-variant' : 'mdi:account-wrench'" :class="['text-xl', f2ModalType === 'productos' ? 'text-blue-600' : 'text-purple-600']" />
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white">Seleccionar {{ f2ModalType === 'productos' ? 'Producto / Equipo' : 'Técnico' }}</h3>
            </div>
            <button @click="cerrarF2Modal" class="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <Icon icon="mdi:close" class="text-xl text-gray-500" />
            </button>
          </div>
          
          <div class="p-4">
            <div class="relative">
              <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input v-model="f2ModalSearch" type="text" :placeholder="'Buscar ' + (f2ModalType === 'productos' ? 'por nombre o código...' : 'técnico por nombre...')" class="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm transition-all" />
            </div>
          </div>

          <div class="flex-1 overflow-y-auto p-2 custom-scrollbar">
            <div class="grid grid-cols-1 gap-2">
              <template v-if="f2ModalType === 'productos'">
                <div v-for="ps in filtradosF2" :key="ps.idProductoServicio" @click="seleccionarDesdeF2(ps)" class="p-3 border border-gray-100 dark:border-gray-700 rounded-xl hover:border-blue-400 dark:hover:border-blue-500 cursor-pointer transition-all hover:shadow-md bg-white dark:bg-gray-800 flex items-center justify-between group">
                  <div class="flex items-center gap-3">
                    <div class="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg group-hover:bg-blue-100">
                      <Icon icon="mdi:barcode" class="text-blue-600" />
                    </div>
                    <div>
                      <p class="font-bold text-sm text-gray-800 dark:text-white">{{ ps.codigo }}</p>
                      <p class="text-xs text-gray-500">{{ ps.nombre }}</p>
                    </div>
                  </div>
                  <Icon icon="mdi:chevron-right" class="text-gray-300 group-hover:text-blue-500" />
                </div>
              </template>
              <template v-else>
                <div v-for="tec in filtradosF2" :key="tec.idTecnico || tec.idUsuario" @click="seleccionarDesdeF2(tec)" class="p-3 border border-gray-100 dark:border-gray-700 rounded-xl hover:border-purple-400 dark:hover:border-purple-500 cursor-pointer transition-all hover:shadow-md bg-white dark:bg-gray-800 flex items-center justify-between group">
                  <div class="flex items-center gap-3">
                    <div class="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg group-hover:bg-purple-100">
                      <Icon icon="mdi:account" class="text-purple-600" />
                    </div>
                    <div>
                      <p class="font-bold text-sm text-gray-800 dark:text-white">{{ tec.nombre?.toUpperCase() }} {{ tec.apellido?.toUpperCase() }}</p>
                      <p class="text-[10px] text-gray-500">{{ tec.nombreUsuario }}</p>
                    </div>
                  </div>
                  <Icon icon="mdi:chevron-right" class="text-gray-300 group-hover:text-purple-500" />
                </div>
              </template>
            </div>

            <div v-if="filtradosF2.length === 0" class="py-12 text-center">
              <Icon icon="mdi:database-search-outline" class="text-5xl text-gray-300 mx-auto mb-2" />
              <p class="text-gray-500 text-sm">No se encontraron resultados</p>
            </div>
          </div>
          
          <div class="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-right">
             <p class="text-[10px] text-gray-400 text-left float-left mt-2">Total: {{ filtradosF2.length }} registros</p>
             <button @click="cerrarF2Modal" class="px-4 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100">CERRAR</button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showPagoExpressModal" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[70]">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn border border-yellow-400/30">
          <div class="bg-gradient-to-r from-yellow-500 to-orange-500 p-5 text-white">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon icon="mdi:cash-fast" class="text-3xl" />
              </div>
              <div>
                <h3 class="text-lg font-bold">Pago de Diagnóstico Express</h3>
                <p class="text-yellow-100 text-xs mt-0.5">El diagnóstico Express requiere pago anticipado</p>
              </div>
            </div>
            <div class="mt-4 bg-white/15 rounded-xl p-3 text-center border border-white/20">
              <p class="text-xs text-yellow-200 uppercase tracking-widest font-medium mb-1">Monto a Cobrar</p>
              <p class="text-4xl font-black tracking-tight">$10.00</p>
              <p class="text-xs text-yellow-100 mt-1">USD — Diagnóstico Prioritario</p>
            </div>
          </div>

          <div class="p-5 space-y-4">
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Método de Pago <span class="text-red-500">*</span></label>
              <div class="grid grid-cols-2 gap-2">
                <button v-for="metodo in metodosPago" :key="metodo.value"
                  @click="pagoExpress.metodoPago = metodo.value"
                  :class="['flex items-center gap-2 p-3 rounded-xl border-2 text-sm font-medium transition-all duration-200', pagoExpress.metodoPago === metodo.value ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 shadow-sm' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-yellow-300 hover:bg-yellow-50/50 dark:hover:bg-yellow-900/10']">
                  <Icon :icon="metodo.icon" class="text-lg flex-shrink-0" />
                  <span class="text-xs">{{ metodo.label }}</span>
                </button>
              </div>
            </div>

            <div v-if="pagoExpress.metodoPago && pagoExpress.metodoPago !== 'EFECTIVO_USD'">
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">N° Referencia / Confirmación</label>
              <div class="relative">
                <Icon icon="mdi:identifier" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input v-model="pagoExpress.referencia" type="text" placeholder="Ej: 123456789" class="w-full pl-9 pr-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all" />
              </div>
            </div>

            <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-3 flex gap-2">
              <Icon icon="mdi:information" class="text-blue-500 text-lg flex-shrink-0 mt-0.5" />
              <p class="text-xs text-blue-700 dark:text-blue-300">Este cobro garantiza atención prioritaria. Si el equipo es reparado, el monto <b>se descuenta</b> del costo total del servicio.</p>
            </div>

            <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 flex items-center gap-3">
              <div class="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Icon icon="mdi:account-check" class="text-green-600 dark:text-green-400 text-base" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-bold text-gray-800 dark:text-white truncate">{{ clienteSeleccionado?.nombre }} {{ clienteSeleccionado?.apellido }}</p>
                <p class="text-[10px] text-gray-500">Orden: {{ numeroOrdenDisplay }}</p>
              </div>
              <div class="text-right">
                <p class="text-xs font-black text-yellow-600">$10.00</p>
                <p class="text-[10px] text-gray-400">Express</p>
              </div>
            </div>
          </div>

          <div class="px-5 pb-5 flex gap-3">
            <button @click="cancelarPagoExpress" class="flex-1 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-medium text-sm">
              Cancelar
            </button>
            <button @click="confirmarPagoExpress" :disabled="!pagoExpress.metodoPago || procesandoPagoExpress"
              :class="['flex-1 px-4 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all duration-200', pagoExpress.metodoPago && !procesandoPagoExpress ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-md hover:shadow-lg hover:from-yellow-600 hover:to-orange-600' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed']">
              <Icon v-if="procesandoPagoExpress" icon="mdi:loading" class="animate-spin" />
              <Icon v-else icon="mdi:check-circle" />
              {{ procesandoPagoExpress ? 'Procesando...' : 'Confirmar Pago y Registrar' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { notificarRecepcionExpress } from '@/utils/notificacionesHelper';
import api from '@/api/recepcionEquipo.js';
import { obtenerClientes, crearCliente, formatearDocumento, formatearTelefonoVenezolano } from '@/api/clientes.js';
import { obtenerProductoServicios } from '@/api/productoServicio.js';
import { obtenerEstadosRecepcion } from '@/api/estadoRecepcion.js';
import { obtenerSedesActivas } from '@/api/sede.js';
import { obtenerUsuarios } from '@/api/usuarios.js';
import { obtenerTecnicos, obtenerTecnicoPorIdUsuario } from '@/api/tecnicos.js';
import { getCurrentUser } from "@/utils/permissionUtils";
import { imprimirTicketDiagnostico } from '@/utils/ticketGenerator.js';
import { toTitleCase } from '@/utils/stringUtils.js';

export default {
  name: 'RecepcionExpress',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      loading: false,
      pollingActive: false,
      pollingInterval: null,
      buscarCliente: '',
      clienteSeleccionado: null,
      clientesEncontrados: [],
      cargandoClientes: false,
      mostrarListaClientes: false,
      timeoutBusqueda: null,
      todosLosClientes: [],
      modoCliente: 'buscar',
      nuevoCliente: { tipoDocumento: '', documento: '', nombre: '', apellido: '', telefono: '', email: '', direccion: '', estado: 'activo' },
      guardandoCliente: false,
      productoServicios: [],
      buscarProducto: '',
      productosEncontrados: [],
      productoSeleccionado: null,
      cargandoProductos: false,
      mostrarListaProductos: false,
      estados: [],
      sedesList: [],
      tecnicos: [],
      buscarTecnico: '',
      tecnicosEncontrados: [],
      cargandoTecnicos: false,
      mostrarListaTecnicos: false,
      tecnicoSeleccionado: null,
      form: {
        idProductoServicios: 0,
        fechaIngreso: new Date().toLocaleString('sv-SE').replace(' ', 'T').slice(0, 16),
        fechaReparacion: '',
        fechaEntrega: '',
        fechaPago: '',
        descripcion: '',
        problema: '',
        accesorios: '',
        idCliente: 0,
        idUsuario: parseInt(localStorage.getItem('idUsuario') || 1),
        idTecnicoAsignado: 0,
        idEstadoRecepcion: 0,
        detalles: [],
        clavePatron: '',
        taller: '',
        idSede: null,
        tipoDiagnostico: 'GRATIS',
        montoDiagnostico: 0,
        actividadesRealizadas: ''
      },
      numeroOrdenDisplay: '',
      showF2Modal: false,
      f2ModalType: 'productos',
      f2ModalSearch: '',
      showConfirmModal: false,
      confirmModalTitle: '',
      confirmModalMessage: '',
      confirmModalIcon: 'mdi:check-circle',
      confirmModalIconClass: 'bg-green-100 dark:bg-green-900/30',
      confirmModalIconColor: 'text-green-600 dark:text-green-400',
      confirmModalConfirmText: 'Aceptar',
      confirmModalCancelText: 'Cancelar',
      confirmModalType: 'success',
      confirmModalConfirmClass: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
      confirmCallback: null,
      confirmCancelCallback: null,
      showPagoExpressModal: false,
      procesandoPagoExpress: false,
      pagoExpress: {
        metodoPago: '',
        referencia: ''
      },
      metodosPago: [
        { value: 'EFECTIVO_USD',    label: 'Efectivo USD',    icon: 'mdi:cash-usd' },
        { value: 'TRANSFERENCIA',   label: 'Transferencia',   icon: 'mdi:bank-transfer' },
        { value: 'PAGO_MOVIL',      label: 'Pago Móvil',     icon: 'mdi:cellphone-nfc' },
        { value: 'ZELLE',           label: 'Zelle',           icon: 'mdi:send-circle' },
        { value: 'BINANCE',         label: 'Binance/Cripto',  icon: 'mdi:currency-btc' },
        { value: 'PUNTO_VENTA',     label: 'Punto de Venta',  icon: 'mdi:credit-card' }
      ],
      currentUser: null
    };
  },
  computed: {
    filtradosF2() {
      const termino = this.f2ModalSearch.toLowerCase().trim();
      if (this.f2ModalType === 'productos') {
        if (!termino) return this.productoServicios.slice(0, 50);
        return this.productoServicios.filter(ps => 
          (ps.nombre || '').toLowerCase().includes(termino) || 
          (ps.codigo || '').toLowerCase().includes(termino)
        ).slice(0, 50);
      } else {
        if (!termino) return this.tecnicos.slice(0, 50);
        return this.tecnicos.filter(tec => 
          `${tec.nombre} ${tec.apellido}`.toLowerCase().includes(termino)
        ).slice(0, 50);
      }
    },
    puedeFinalizar() {
      return (
        this.clienteSeleccionado &&
        this.form.idProductoServicios !== 0 &&
        this.form.descripcion.trim() !== '' &&
        this.form.problema.trim() !== '' &&
        this.form.idTecnicoAsignado !== 0 &&
        this.form.idSede !== null &&
        this.form.idSede !== 'null' &&
        this.form.accesorios.trim() !== ''
      );
    },
    puedeGuardarCliente() { 
      return (this.nuevoCliente.tipoDocumento && this.nuevoCliente.documento && this.nuevoCliente.documento.trim() !== '' && this.nuevoCliente.nombre && this.nuevoCliente.nombre.trim() !== '' && this.nuevoCliente.direccion && this.nuevoCliente.direccion.trim() !== ''); 
    }
  },
  async mounted() {
    await this.cargarCatalogos();
    await this.generarNumeroOrdenDisplay();
    
    // 🔥 INICIAR POLLING CADA 10 SEGUNDOS
    this.pollingActive = true;
    this.pollingInterval = setInterval(() => {
      this.generarNumeroOrdenDisplay();
    }, 10000);
  },
  beforeDestroy() {
    // 🔥 LIMPIAR EL INTERVALO AL DESTRUIR EL COMPONENTE
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      this.pollingActive = false;
    }
  },
  methods: {
    toTitleCase,
    mostrarConfirmacion(options) {
      this.confirmModalTitle = options.title || 'Confirmar';
      this.confirmModalMessage = options.message || '¿Estás seguro?';
      this.confirmModalIcon = options.icon || 'mdi:help-circle';
      this.confirmModalIconClass = options.iconClass || 'bg-yellow-100 dark:bg-yellow-900/30';
      this.confirmModalIconColor = options.iconColor || 'text-yellow-600 dark:text-yellow-400';
      this.confirmModalConfirmText = options.confirmText || 'Aceptar';
      this.confirmModalCancelText = options.cancelText || 'Cancelar';
      this.confirmModalType = options.type || 'confirm';
      if (options.type === 'danger') this.confirmModalConfirmClass = 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700';
      else if (options.type === 'success') this.confirmModalConfirmClass = 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700';
      else this.confirmModalConfirmClass = 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700';
      this.confirmCallback = options.onConfirm || null;
      this.confirmCancelCallback = options.onCancel || null;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() { this.showConfirmModal = false; if (this.confirmCancelCallback) this.confirmCancelCallback(); },
    aceptarConfirmacion() { this.showConfirmModal = false; if (this.confirmCallback) this.confirmCallback(); },

    mostrarModalF2Productos() {
      this.f2ModalType = 'productos';
      this.f2ModalSearch = '';
      this.showF2Modal = true;
    },
    mostrarModalF2Tecnicos() {
      this.f2ModalType = 'tecnicos';
      this.f2ModalSearch = '';
      this.showF2Modal = true;
    },
    cerrarF2Modal() { this.showF2Modal = false; },
    seleccionarDesdeF2(item) {
      if (this.f2ModalType === 'productos') {
        this.seleccionarProducto(item);
      } else {
        this.seleccionarTecnico(item);
      }
      this.cerrarF2Modal();
    },

    async cargarCatalogos() {
      console.log("🚀 Cargando catálogos para RecepcionExpress...");
      
      const executeApi = async (apiFunc, label) => {
        try {
          const res = await apiFunc();
          if (!res.success) console.warn(`⚠️ ${label} cargado con advertencia:`, res.error || res.message);
          return res;
        } catch (err) {
          console.error(`❌ Error cargando ${label}:`, err);
          return { success: false, error: err.message, data: [] };
        }
      };

      const [cRes, uRes, eRes, psRes, tRes, sdRes] = await Promise.all([
        executeApi(() => obtenerClientes(), "Clientes"),
        executeApi(() => obtenerUsuarios('activos'), "Usuarios"),
        executeApi(() => obtenerEstadosRecepcion(true), "Estados"),
        executeApi(() => obtenerProductoServicios(), "Productos/Servicios"),
        executeApi(() => obtenerTecnicos(), "Técnicos"),
        executeApi(() => obtenerSedesActivas(), "Sedes")
      ]);
      
      if (cRes.success) this.todosLosClientes = cRes.data || [];
      if (sdRes.success) this.sedesList = sdRes.data || [];
      if (psRes.success) this.productoServicios = psRes.data || [];
      if (tRes.success) this.tecnicos = tRes.data || [];

      this.currentUser = getCurrentUser();
      if (this.currentUser) {
        const idUsuario = this.currentUser.idUsuario || this.currentUser.id;
        const tecRes = await obtenerTecnicoPorIdUsuario(idUsuario);
        if (tecRes.success && tecRes.data) {
          this.seleccionarTecnico(tecRes.data);
        } else {
          this.tecnicoSeleccionado = {
            nombre: this.currentUser.nombre || 'Admin',
            apellido: this.currentUser.apellido || '',
            idTecnico: 0
          };
          this.form.idTecnicoAsignado = this.currentUser.idUsuario || this.currentUser.id;
        }
      }
      
      if (tRes.success && tRes.data?.length > 0) {
        this.tecnicos = tRes.data;
      } else if (uRes.success && uRes.data?.length > 0) {
        this.tecnicos = uRes.data.filter(u => 
          u.idRol === 3 || u.rolId === 3 || u.id_rol === 3 || u.tipo_tecnico === true ||
          u.nombre?.toLowerCase().includes('tec') ||
          (u.rol && u.rol.nombre?.toLowerCase().includes('tec'))
        );
      }

      if (this.tecnicos.length > 0) {
        this.tecnicosEncontrados = this.tecnicos.slice(0, 10);
        const idUsuarioActual = parseInt(localStorage.getItem('idUsuario'));
        if (idUsuarioActual && !this.form.idTecnicoAsignado) {
          const tecActual = this.tecnicos.find(t => t.idUsuario === idUsuarioActual);
          if (tecActual) this.seleccionarTecnico(tecActual);
        }
      }

      if (eRes.success && eRes.data?.length > 0) {
        this.estados = eRes.data;
        const estPorDiag = this.estados.find(e => e.nombre?.toUpperCase().includes('POR DIAGNOSTICAR'));
        if (estPorDiag) {
          this.form.idEstadoRecepcion = estPorDiag.idEstadoRecepcion;
        } else {
          this.form.idEstadoRecepcion = this.estados[0].idEstadoRecepcion;
        }
      }
      
      console.log("✅ Catálogos procesados:", {
        clientes: this.todosLosClientes.length,
        tecnicos: this.tecnicos.length,
        productos: this.productoServicios.length,
        sedes: this.sedesList.length
      });
    },

    // 🔥 GENERAR NÚMERO DE ORDEN PARA MOSTRAR EN UI
    async generarNumeroOrdenDisplay() {
      try {
        const res = await api.obtenerUltimoNumeroOrden();
        if (res.success) {
          const lastNum = parseInt(res.data);
          const nextNum = (isNaN(lastNum) || lastNum < 5999) ? 6000 : lastNum + 1;
          this.numeroOrdenDisplay = nextNum.toString();
          console.log(`📊 [FRONTEND] Último número: ${lastNum}, Siguiente: ${nextNum}`);
        } else {
          this.numeroOrdenDisplay = '6000';
        }
      } catch (error) {
        console.error("Error generando número de orden:", error);
        this.numeroOrdenDisplay = '6000';
      }
    },

    debounceBuscarClientes() { this.mostrarListaClientes = true; if (this.timeoutBusqueda) clearTimeout(this.timeoutBusqueda); this.timeoutBusqueda = setTimeout(() => this.buscarClientesLocal(), 300); },
    mostrarClientesSiHay() {
      if (!this.buscarCliente || this.buscarCliente.trim() === '') {
        this.clientesEncontrados = this.todosLosClientes.slice(0, 10);
      }
      if (this.clientesEncontrados.length > 0) this.mostrarListaClientes = true;
    },
    buscarClientesLocal() { 
      if (!this.buscarCliente || this.buscarCliente.trim() === '') {
        this.clientesEncontrados = this.todosLosClientes.slice(0, 10);
        this.mostrarListaClientes = this.clientesEncontrados.length > 0;
        this.cargandoClientes = false;
        return;
      } 
      this.cargandoClientes = true; 
      const termino = this.buscarCliente.toLowerCase().trim(); 
      setTimeout(() => { 
        this.clientesEncontrados = this.todosLosClientes.filter(cliente => { 
          const nombreCompleto = `${cliente.nombre || ''} ${cliente.apellido || ''}`.toLowerCase(); 
          const documento = (cliente.documento || '').toLowerCase();
          const telefono = (cliente.telefono || '').toLowerCase();
          const email = (cliente.email || '').toLowerCase();
          return nombreCompleto.includes(termino) || documento.includes(termino) || telefono.includes(termino) || email.includes(termino);
        }).slice(0, 10); 
        this.cargandoClientes = false; 
      }, 300); 
    },

    debounceBuscarProductos() { this.mostrarListaProductos = true; if (this.timeoutBusqueda) clearTimeout(this.timeoutBusqueda); this.timeoutBusqueda = setTimeout(() => this.buscarProductosLocal(), 300); },
    mostrarProductosSiHay() { if (this.productosEncontrados.length > 0) this.mostrarListaProductos = true; },
    buscarProductosLocal() {
      if (!this.buscarProducto || this.buscarProducto.trim() === '') { this.productosEncontrados = []; this.mostrarListaProductos = false; return; }
      this.cargandoProductos = true;
      const termino = this.buscarProducto.toLowerCase().trim();
      setTimeout(() => {
        this.productosEncontrados = this.productoServicios.filter(ps => {
          const normalize = (str) => (str || '').toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          const terms = normalize(this.buscarProducto).split(/\s+/).filter(Boolean);
          const searchableText = normalize(`${ps.nombre} ${ps.codigo || ''} ${ps.descripcion || ''}`);
          return terms.every(term => searchableText.includes(term));
        }).slice(0, 15);
        this.cargandoProductos = false;
      }, 300);
    },

    debounceBuscarTecnicos() { this.mostrarListaTecnicos = true; if (this.timeoutBusqueda) clearTimeout(this.timeoutBusqueda); this.timeoutBusqueda = setTimeout(() => this.buscarTecnicosLocal(), 300); },
    mostrarTecnicosSiHay() { 
      if (this.tecnicos.length > 0) {
        if (!this.buscarTecnico) {
          this.tecnicosEncontrados = this.tecnicos.slice(0, 10);
        }
        this.mostrarListaTecnicos = true; 
      }
    },
    buscarTecnicosLocal() {
      if (!this.buscarTecnico || this.buscarTecnico.trim() === '') { 
        this.tecnicosEncontrados = this.tecnicos.slice(0, 10); 
        return; 
      }
      this.cargandoTecnicos = true;
      const termino = this.buscarTecnico.toLowerCase().trim();
      setTimeout(() => {
        this.tecnicosEncontrados = this.tecnicos.filter(tec => {
          const nombreCompleto = `${tec.nombre || ''} ${tec.apellido || ''}`.toLowerCase();
          return nombreCompleto.includes(termino);
        }).slice(0, 10);
        this.cargandoTecnicos = false;
      }, 300);
    },

    seleccionarTecnico(tec) {
      this.tecnicoSeleccionado = tec;
      this.form.idTecnicoAsignado = tec.idTecnico || tec.idUsuario;
      this.buscarTecnico = '';
      this.tecnicosEncontrados = [];
      this.mostrarListaTecnicos = false;
      this.toast.success(`Técnico ${tec.nombre} asignado`);
    },

    deseleccionarTecnico() {
      this.tecnicoSeleccionado = null;
      this.form.idTecnicoAsignado = 0;
      this.buscarTecnico = '';
      this.toast.info('Técnico removido');
    },

    seleccionarProducto(ps) {
      this.productoSeleccionado = ps;
      this.form.idProductoServicios = ps.idProductoServicios || ps.idProductoServicio || ps.id || 0;
      this.form.descripcion = ps.descripcion || '';
      this.buscarProducto = '';
      this.productosEncontrados = [];
      this.mostrarListaProductos = false;
      this.toast.success(`Producto ${ps.nombre} seleccionado`);
    },

    deseleccionarProducto() {
      this.productoSeleccionado = null;
      this.form.idProductoServicios = 0;
      this.form.descripcion = '';
      this.buscarProducto = '';
      this.toast.info('Producto removido');
    },

    seleccionarCliente(cliente) {
      if (!cliente) return;
      console.log("🎯 Seleccionando cliente:", cliente);
      const id = cliente.idCliente || cliente.id || cliente.id_cliente;
      if (!id) {
        console.error("❌ El cliente seleccionado no tiene un ID válido:", cliente);
        this.toast.error("Error al seleccionar: ID de cliente no encontrado");
        return;
      }
      this.clienteSeleccionado = { ...cliente, idCliente: id };
      this.form.idCliente = id;
      this.buscarCliente = '';
      this.clientesEncontrados = [];
      this.mostrarListaClientes = false;
      console.log("✅ Cliente seleccionado exitosamente:", this.clienteSeleccionado);
      this.toast.success(`Cliente ${cliente.nombre || 'seleccionado'} activado`);
    },

    deseleccionarCliente() { 
      this.clienteSeleccionado = null; 
      this.form.idCliente = null; 
      this.buscarCliente = ''; 
      this.toast.info('Cliente removido'); 
    },

    async guardarNuevoCliente() {
      if (!this.puedeGuardarCliente) { 
        this.toast.error('Por favor complete los campos obligatorios (Tipo, Documento, Nombre y Dirección)'); 
        return; 
      }
      this.guardandoCliente = true;
      try {
        const response = await crearCliente(this.nuevoCliente);
        if (response.success) {
          let clienteParaSeleccionar = response.data || { ...this.nuevoCliente };
          const buscarId = (obj) => {
            if (!obj) return null;
            return obj.idCliente || obj.id || obj.id_cliente || obj.IdCliente || obj.Id || obj.ID || obj.id_Cliente;
          };
          const idRespuesta = buscarId(response) || buscarId(response.data);
          if (idRespuesta) {
            clienteParaSeleccionar.idCliente = idRespuesta;
          }
          clienteParaSeleccionar.nombre = clienteParaSeleccionar.nombre || this.nuevoCliente.nombre;
          clienteParaSeleccionar.apellido = clienteParaSeleccionar.apellido || this.nuevoCliente.apellido;
          clienteParaSeleccionar.documento = clienteParaSeleccionar.documento || this.nuevoCliente.documento;
          clienteParaSeleccionar.tipoDocumento = clienteParaSeleccionar.tipoDocumento || this.nuevoCliente.tipoDocumento;

          if (Array.isArray(this.todosLosClientes)) {
            const existe = this.todosLosClientes.find(c => c.idCliente === clienteParaSeleccionar.idCliente);
            if (!existe) {
              this.todosLosClientes.push(clienteParaSeleccionar);
            }
          }
          this.modoCliente = 'buscar';
          setTimeout(() => {
            this.seleccionarCliente(clienteParaSeleccionar);
          }, 100);
          this.nuevoCliente = { 
            tipoDocumento: 'cedula', 
            documento: '', 
            nombre: '', 
            apellido: '', 
            telefono: '', 
            email: '', 
            direccion: '', 
            estado: 'activo' 
          };
          this.toast.success(`Cliente "${clienteParaSeleccionar.nombre}" creado exitosamente`);
        } else {
          throw new Error(response.error || response.message || 'Error al procesar la respuesta del servidor');
        }
      } catch (error) {
        console.error("❌ [ERROR] Falló la creación del cliente:", error);
        this.toast.error('No se pudo crear el cliente: ' + (error.message || 'Error de comunicación'));
      } finally {
        this.guardandoCliente = false;
      }
    },

    formatearTipoDocumento(tipo) { const tipos = { 'cedula': 'Cédula', 'rif': 'RIF', 'pasaporte': 'Pasaporte', 'identificacion_extranjera': 'Identificación Extranjera' }; return tipos[tipo?.toLowerCase()] || tipo || 'Sin documento'; },
    getPlaceholderDocumento(tipo) { const placeholders = { 'cedula': 'V-31.757.818', 'rif': 'J-12345678-9', 'pasaporte': 'ABC123456', 'identificacion_extranjera': 'Número' }; return placeholders[tipo] || 'Documento'; },
    formatearDocumentoInput() { if (this.nuevoCliente.tipoDocumento && this.nuevoCliente.documento) this.nuevoCliente.documento = formatearDocumento(this.nuevoCliente.tipoDocumento, this.nuevoCliente.documento); },
    formatearTelefonoInput() { if (this.nuevoCliente.telefono) this.nuevoCliente.telefono = formatearTelefonoVenezolano(this.nuevoCliente.telefono); },
    limitarAccesorios() {
      if (this.form.accesorios && this.form.accesorios.length > 200) {
        this.form.accesorios = this.form.accesorios.slice(0, 200);
      }
    },

    async submitRecepcion() {
      if (!this.puedeFinalizar) {
        this.toast.error('Por favor complete los campos obligatorios');
        return;
      }
      if (this.form.tipoDiagnostico === 'EXPRESS') {
        this.pagoExpress = { metodoPago: '', referencia: '' };
        this.showPagoExpressModal = true;
        return;
      }
      this.mostrarConfirmacion({
        title: 'Confirmar Recepción',
        message: `¿Desea registrar la orden <b>${this.numeroOrdenDisplay}</b> para <b>${this.clienteSeleccionado.nombre}</b>?`,
        icon: 'mdi:flash',
        confirmText: 'Sí, registrar',
        onConfirm: () => this._procesarRegistroRecepcion()
      });
    },

    cancelarPagoExpress() {
      this.showPagoExpressModal = false;
      this.pagoExpress = { metodoPago: '', referencia: '' };
    },

    async confirmarPagoExpress() {
      if (!this.pagoExpress.metodoPago) {
        this.toast.warning('Seleccione un método de pago para continuar');
        return;
      }
      this.showPagoExpressModal = false;
      await this._procesarRegistroRecepcion();
    },

    // 🔥 PRINCIPAL: Procesa el registro SIN enviar numeroOrden al backend
    async _procesarRegistroRecepcion() {
      this.loading = true;
      try {
        if (this.form.accesorios && this.form.accesorios.length > 200) {
          this.form.accesorios = this.form.accesorios.slice(0, 200);
        }
        
        const payload = {
          ...this.form,
          clavePatron: (this.form.accesorios || '').slice(0, 100),
          fechaIngreso: new Date(this.form.fechaIngreso).toLocaleString('sv-SE').replace(' ', 'T'),
          fechaReparacion: this.form.fechaReparacion ? new Date(this.form.fechaReparacion).toLocaleString('sv-SE').replace(' ', 'T') : null,
          fechaEntrega: this.form.fechaEntrega ? new Date(this.form.fechaEntrega).toLocaleString('sv-SE').replace(' ', 'T') : null,
          fechaPago: this.form.fechaPago ? new Date(this.form.fechaPago).toLocaleString('sv-SE').replace(' ', 'T') : null,
          idTecnicoAsignado: this.form.idTecnicoAsignado !== 0 ? this.form.idTecnicoAsignado : null,
          idProductoServicios: this.form.idProductoServicios !== 0 ? this.form.idProductoServicios : null,
          idSede: this.form.idSede !== 'null' ? parseInt(this.form.idSede) : null,
          accesorios: this.form.accesorios || '',
          detalles: [],
          metodoPagoExpress: this.pagoExpress.metodoPago || null,
          referenciaPagoExpress: this.pagoExpress.referencia || null
        };

        console.log('📤 Enviando payload SIN numeroOrden:', payload);

        const res = await api.crearRecepcion(payload);

        if (res.success) {
          const recepcionCreada = res.data || payload;
          const numeroOrdenGenerado = recepcionCreada.numeroOrden || recepcionCreada.NumeroOrden || this.numeroOrdenDisplay;

          if (this.form.tipoDiagnostico === 'EXPRESS') {
            const metodoPagoLabel = this.metodosPago.find(m => m.value === this.pagoExpress.metodoPago)?.label || this.pagoExpress.metodoPago;
            this.toast.success(`✅ Pago de $10 registrado (${metodoPagoLabel}) — Orden #${numeroOrdenGenerado}`);
            notificarRecepcionExpress('Recepción Express creada', `Orden #${numeroOrdenGenerado}`, `Cliente: ${this.clienteSeleccionado?.nombre} · Pago: ${metodoPagoLabel}`);
          } else {
            this.toast.success(`✅ Recepción #${numeroOrdenGenerado} registrada exitosamente`);
            notificarRecepcionExpress('Recepción Gratis creada', `Orden #${numeroOrdenGenerado}`, `Cliente: ${this.clienteSeleccionado?.nombre}`);
          }

          const tecnico = this.tecnicos.find(t => t.idUsuario === this.form.idTecnicoAsignado);
          const categoria = this.productoServicios.find(ps => ps.idProductoServicio === this.form.idProductoServicios);

          const ordenParaTicket = {
            ...recepcionCreada,
            equipo: categoria?.nombre || 'EQUIPO GENERICO',
            descripcion: this.form.descripcion,
            problema: this.form.problema,
            accesorios: this.form.accesorios,
            numeroOrden: numeroOrdenGenerado,
            fechaIngreso: payload.fechaIngreso,
            fechaReparacion: payload.fechaReparacion,
            fechaEntrega: payload.fechaEntrega,
            fechaPago: payload.fechaPago,
            montoDiagnostico: this.form.montoDiagnostico,
            tipoDiagnostico: this.form.tipoDiagnostico,
            metodoPagoExpress: this.pagoExpress.metodoPago || null
          };

          imprimirTicketDiagnostico(ordenParaTicket, this.clienteSeleccionado, tecnico);

          // 🔥 ACTUALIZAR EL NÚMERO DE ORDEN EN EL FRONTEND DESPUÉS DE CREAR
          await this.generarNumeroOrdenDisplay();

          this.pagoExpress = { metodoPago: '', referencia: '' };
          this.resetForm();
        } else {
          this.toast.error(res.error || 'Error al registrar la recepción');
        }
      } catch (error) {
        console.error(error);
        this.toast.error('Error crítico al conectar con el servidor');
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        ...this.form,
        idProductoServicios: 0,
        fechaIngreso: new Date().toLocaleString('sv-SE').replace(' ', 'T').slice(0, 16),
        fechaReparacion: '',
        fechaEntrega: '',
        fechaPago: '',
        descripcion: '',
        problema: '',
        accesorios: '',
        idCliente: 0,
        idTecnicoAsignado: 0,
        clavePatron: '',
        taller: '',
        idSede: null,
        tipoDiagnostico: 'GRATIS',
        montoDiagnostico: 0,
        actividadesRealizadas: ''
      };
      this.clienteSeleccionado = null;
      this.productoSeleccionado = null;
      this.tecnicoSeleccionado = null;
      this.buscarProducto = '';
      this.buscarCliente = '';
      this.buscarTecnico = '';
      this.generarNumeroOrdenDisplay();
    },

    actualizarMontoDiagnostico() {
      if (this.form.tipoDiagnostico === 'EXPRESS') {
        this.form.montoDiagnostico = 10;
      } else {
        this.form.montoDiagnostico = 0;
        this.pagoExpress = { metodoPago: '', referencia: '' };
      }
    },

    actualizarNombreTaller() {
      if (this.form.idSede === 'null' || this.form.idSede === null) {
        this.form.taller = '';
        return;
      }
      const sede = this.sedesList.find(s => s.idSede === parseInt(this.form.idSede));
      if (sede) {
        this.form.taller = sede.nombre;
        console.log("🏪 Taller actualizado a:", sede.nombre);
      }
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
</style>